import { RANK_ORDER_STANDARD } from './constants';
import { Rank, Suit } from './enums';
import { Card } from './types';

/**
 * Creates a card with the given rank and suit
 */
export function createCard(rank: Rank, suit: Suit): Card {
  return { rank, suit };
}

/**
 * Parses a card string (e.g., "As" → Ace of Spades)
 * Format: RankSuit where Rank is A,2-9,T,J,Q,K and Suit is c,d,h,s
 */
export function parseCard(str: string): Card {
  if (str.length !== 2) {
    throw new Error(`Invalid card string: ${str}`);
  }

  const rankChar = str[0];
  const suitChar = str[1];

  const rank = Object.values(Rank).find((r) => r === rankChar);
  const suit = Object.values(Suit).find((s) => s === suitChar);

  if (!rank) {
    throw new Error(`Invalid rank: ${rankChar}`);
  }
  if (!suit) {
    throw new Error(`Invalid suit: ${suitChar}`);
  }

  return createCard(rank, suit);
}

/**
 * Parses multiple cards from a string (e.g., "AsKhQd" → [As, Kh, Qd])
 */
export function parseCards(str: string): Card[] {
  if (str.length % 2 !== 0) {
    throw new Error(`Invalid cards string length: ${str}`);
  }

  const cards: Card[] = [];
  for (let i = 0; i < str.length; i += 2) {
    cards.push(parseCard(str.slice(i, i + 2)));
  }
  return cards;
}

/**
 * Converts a card to its string representation (e.g., Ace of Spades → "As")
 */
export function cardToString(card: Card): string {
  return `${card.rank}${card.suit}`;
}

/**
 * Converts multiple cards to a string (e.g., [As, Kh] → "AsKh")
 */
export function cardsToString(cards: readonly Card[]): string {
  return cards.map(cardToString).join('');
}

/**
 * Gets the rank index in standard ordering (higher = stronger)
 */
export function getRankIndex(rank: Rank, rankOrder: readonly Rank[] = RANK_ORDER_STANDARD): number {
  return rankOrder.indexOf(rank);
}

/**
 * Compares two cards by rank (returns negative if a < b, positive if a > b, 0 if equal)
 * Uses standard rank ordering by default
 */
export function compareCards(a: Card, b: Card, rankOrder: readonly Rank[] = RANK_ORDER_STANDARD): number {
  return getRankIndex(a.rank, rankOrder) - getRankIndex(b.rank, rankOrder);
}

/**
 * Checks if two cards are equal (same rank and suit)
 */
export function cardsEqual(a: Card, b: Card): boolean {
  return a.rank === b.rank && a.suit === b.suit;
}

/**
 * Checks if a card is a joker
 */
export function isJoker(card: Card): boolean {
  return card.rank === Rank.JOKER || card.suit === Suit.WILD;
}

/**
 * Sorts cards by rank (highest first)
 */
export function sortCardsByRank(cards: readonly Card[], rankOrder: readonly Rank[] = RANK_ORDER_STANDARD): Card[] {
  return [...cards].sort((a, b) => compareCards(b, a, rankOrder));
}
